/**
 * TankSelectionPopover - 鱼缸选择浮层
 * 类似 ToolboxPreviewPanel 的名片大小浮层组件
 * 展示用户的所有鱼缸，支持快速选择
 * 提供管理入口按钮，点击后在新窗口打开鱼缸管理页面
 *
 * @see Requirements 1.1, 1.2, 1.3, 2.1, 3.1
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Droplets,
  AlertCircle,
  ExternalLink,
  Loader2,
  Plus,
  Check,
  RefreshCw,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import tankService, { Tank } from "@/services/tankService";
import TankCreationForm from "@/components/assessments/TankCreationForm";
import { toast } from "sonner";

// 水体类型颜色映射
const WATER_TYPE_COLORS: Record<string, string> = {
  freshwater:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  saltwater: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  brackish: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
};

// Props 接口
export interface TankSelectionPopoverProps {
  /** 当前选中的鱼缸 */
  selectedTank: Tank | null;
  /** 选择鱼缸回调 */
  onSelect: (tank: Tank) => void;
  /** 浮层是否打开 */
  open: boolean;
  /** 浮层开关状态变化回调 */
  onOpenChange: (open: boolean) => void;
  /** 触发器子元素 */
  children: React.ReactNode;
  /** 浮层对齐方式 */
  align?: "start" | "center" | "end";
  /** 浮层显示侧 */
  side?: "top" | "right" | "bottom" | "left";
  /** 侧边偏移量 */
  sideOffset?: number;
}

/**
 * 获取水体类型颜色
 */
function getWaterTypeColor(waterType: string): string {
  return (
    WATER_TYPE_COLORS[waterType] ||
    "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
  );
}

/**
 * 鱼缸卡片项组件
 */
interface TankCardItemProps {
  tank: Tank;
  isSelected: boolean;
  onClick: () => void;
}

const TankCardItem: React.FC<TankCardItemProps> = ({
  tank,
  isSelected,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-start gap-3 p-2.5 rounded-lg text-left transition-colors",
        "hover:bg-muted/80",
        isSelected && "bg-primary/5 border border-primary/20",
      )}
    >
      {/* 封面图或默认图标 */}
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted/60 flex items-center justify-center overflow-hidden">
        {tank.cover_image_url ? (
          <img
            src={tank.cover_image_url}
            alt={tank.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Droplets className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      {/* 鱼缸信息 */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-foreground truncate">
            {tank.name}
          </span>
          {isSelected && (
            <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-xs text-muted-foreground">
            {tank.effective_volume}L
          </span>
          <Badge
            variant="secondary"
            className={cn(
              "text-[10px] px-1.5 py-0",
              getWaterTypeColor(tank.water_type),
            )}
          >
            {tank.water_type_text}
          </Badge>
        </div>
      </div>
    </button>
  );
};

const TankSelectionPopover: React.FC<TankSelectionPopoverProps> = ({
  selectedTank,
  onSelect,
  open,
  onOpenChange,
  children,
  align = "end",
  side = "right",
  sideOffset = 8,
}) => {
  const { t } = useTranslation("assessments");
  const queryClient = useQueryClient();

  // 创建鱼缸 Dialog 状态
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);

  // 获取鱼缸列表
  const {
    data: tanksData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["tanks-for-popover"],
    queryFn: () => tankService.getTanks(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const tanks = tanksData?.tanks || [];
  const isEmpty = tanks.length === 0;

  // 计算内容区域高度（根据鱼缸数量自适应）
  const getContentHeight = () => {
    if (isEmpty || isError || isLoading) return "auto";

    // 每个鱼缸卡片约 60px（包含间距）
    const itemHeight = 60;
    const maxVisibleItems = 6; // 最多显示 6 个不滚动
    const visibleItems = Math.min(tanks.length, maxVisibleItems);
    const contentHeight = visibleItems * itemHeight + 32; // 32px 为 padding

    return `${contentHeight}px`;
  };

  // 处理鱼缸选择
  const handleTankSelect = (tank: Tank) => {
    onSelect(tank);
    onOpenChange(false);
  };

  // 打开鱼缸管理页面
  const handleManageTanks = () => {
    window.open("/app/tanks", "_blank");
  };

  // 打开创建鱼缸 Dialog
  const handleCreateTank = () => {
    setShowCreateDialog(true);
  };

  // 创建鱼缸成功回调
  const handleCreateSuccess = (tank: Tank) => {
    toast.success(t("tankCreation.success", "鱼缸创建成功"));
    setShowCreateDialog(false);
    // 刷新鱼缸列表
    queryClient.invalidateQueries({ queryKey: ["tanks-for-popover"] });
    // 自动选择新创建的鱼缸
    onSelect(tank);
    onOpenChange(false);
  };

  // 渲染加载状态
  const renderLoading = () => (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
    </div>
  );

  // 渲染错误状态
  const renderError = () => (
    <div className="flex flex-col items-center justify-center py-6 text-center px-4">
      <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
        <AlertCircle className="h-5 w-5 text-destructive" />
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        {t("tankPopover.loadError", "加载失败，请稍后重试")}
      </p>
      <Button variant="outline" size="sm" onClick={() => refetch()}>
        {t("tankPopover.retry", "重试")}
      </Button>
    </div>
  );

  // 渲染空状态
  const renderEmpty = () => (
    <div className="flex flex-col items-center justify-center py-6 text-center px-4">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <Droplets className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">
        {t("tankPopover.empty", "还没有鱼缸")}
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
        {t("tankPopover.emptyHint", "创建一个鱼缸，获得更精准的评估建议")}
      </p>
      <Button
        variant="outline"
        size="sm"
        className="gap-1"
        onClick={handleCreateTank}
      >
        <Plus className="h-3.5 w-3.5" />
        {t("tankPopover.createTank", "创建鱼缸")}
      </Button>
    </div>
  );

  // 渲染鱼缸列表
  const renderTankList = () => (
    <div className="space-y-1">
      {tanks.map((tank) => (
        <TankCardItem
          key={tank.id}
          tank={tank}
          isSelected={selectedTank?.id === tank.id}
          onClick={() => handleTankSelect(tank)}
        />
      ))}
    </div>
  );

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          className="w-80 p-0 shadow-xl border-none bg-transparent"
          align={align}
          side={side}
          sideOffset={sideOffset}
        >
          <Card className="border shadow-none overflow-hidden flex flex-col">
            <CardHeader className="p-4 border-b bg-muted/30 pb-3 space-y-0 relative">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Droplets className="h-4 w-4 text-primary" />
                </div>
                <div className="space-y-0.5 min-w-0 pr-8">
                  <CardTitle className="text-sm font-semibold truncate">
                    {t("tankPopover.title", "我的鱼缸")}
                  </CardTitle>
                  {!isLoading && !isError && !isEmpty && (
                    <CardDescription className="text-[10px]">
                      {t("tankPopover.tankCount", "{{count}} 个鱼缸", {
                        count: tanks.length,
                      })}
                    </CardDescription>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 absolute right-3 top-4.5"
                onClick={(e) => {
                  e.stopPropagation();
                  refetch();
                }}
                disabled={isFetching}
                title={t("tankPopover.refresh", "刷新")}
              >
                <RefreshCw
                  className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
                />
              </Button>
            </CardHeader>

            <CardContent className="p-0">
              {!isEmpty && !isError && !isLoading ? (
                <div
                  style={{
                    height: getContentHeight(),
                    maxHeight: "400px",
                  }}
                >
                  <ScrollArea className="h-full">
                    <div className="p-4">{renderTankList()}</div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="p-4">
                  {isLoading && renderLoading()}
                  {isError && !isLoading && renderError()}
                  {!isLoading && !isError && isEmpty && renderEmpty()}
                </div>
              )}
            </CardContent>

            <CardFooter className="p-3 border-t bg-muted/10">
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 text-xs gap-1.5 hover:bg-muted hover:text-foreground transition-colors"
                  onClick={handleCreateTank}
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t("tankPopover.createTank", "创建鱼缸")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 h-8 text-xs gap-1.5 hover:bg-muted hover:text-foreground transition-colors"
                  onClick={handleManageTanks}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t("tankPopover.manageTanks", "管理鱼缸")}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </PopoverContent>
      </Popover>

      {/* 创建鱼缸 Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("tankCreation.title", "创建新鱼缸")}</DialogTitle>
            <DialogDescription>
              {t(
                "tankCreation.description",
                "填写鱼缸信息，获得更精准的评估建议",
              )}
            </DialogDescription>
          </DialogHeader>
          {tanksData && (
            <TankCreationForm
              onSuccess={handleCreateSuccess}
              onCancel={() => setShowCreateDialog(false)}
              filterSystemOptions={tanksData.filter_system_options}
              waterTypeOptions={tanksData.water_type_options}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TankSelectionPopover;
